import { Development, Project } from '../types/Project';
import { ENV } from '../config/env';
import { resolveFileUrl } from './resolveFileUrl';

// Función para convertir Development a Project para compatibilidad temporal
export const developmentToProject = (dev: Development): Project => {
  const coverImage = dev.images?.find(img => img.is_cover) || dev.images?.[0];
  const rawOg = typeof dev.og_image_url === 'string' ? dev.og_image_url.trim() : '';
  const rawCover = typeof (dev as any).cover_image_url === 'string' ? String((dev as any).cover_image_url).trim() : '';
  const rawImage = typeof (dev as any).image_url === 'string' ? String((dev as any).image_url).trim() : '';

  const normalizeMexico = (value?: string | null) => {
    if (!value) return '';
    const deaccented = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return deaccented.replace(/M[^A-Za-z]*xico/gi, 'México');
  };

  const toNumber = (value: any): number | null => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    if (typeof value === 'object') {
      if (typeof (value as any).toNumber === 'function') {
        const parsed = Number((value as any).toNumber());
        return Number.isFinite(parsed) ? parsed : null;
      }
      if (typeof (value as any).toString === 'function') {
        const parsed = Number((value as any).toString());
        return Number.isFinite(parsed) ? parsed : null;
      }
    }
    return null;
  };

  const resolveImageUrl = (value?: string | null) => {
    if (!value) return '';
    const trimmed = String(value).trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;

    const apiBase = typeof ENV.API_URL === 'string'
      ? ENV.API_URL.replace(/\/api\/?$/, '')
      : '';

    if (!apiBase) return trimmed;

    if (trimmed.startsWith('/public/')) return `${apiBase}${trimmed}`;
    if (trimmed.startsWith('/images/')) return `${apiBase}/public${trimmed}`;
    if (trimmed.startsWith('images/')) return `${apiBase}/public/${trimmed}`;
    if (trimmed.startsWith('public/')) return `${apiBase}/${trimmed}`;

    return `${apiBase}/${trimmed.replace(/^\/+/g, '')}`;
  };

  const imageUrl =
    resolveImageUrl(coverImage?.image_url) ||
    resolveImageUrl(rawOg) ||
    resolveImageUrl(rawCover) ||
    resolveImageUrl(rawImage) ||
    '/Profunding_FondoDegradado.png';

  const sortedImages = Array.isArray(dev.images)
    ? [...dev.images].sort((a, b) => {
        if (a.is_cover && !b.is_cover) return -1;
        if (!a.is_cover && b.is_cover) return 1;
        const aOrder = Number(a.sort_order ?? 0);
        const bOrder = Number(b.sort_order ?? 0);
        return aOrder - bOrder;
      })
    : [];

  const galleryList = sortedImages
    .map(img => resolveImageUrl(img.image_url))
    .filter(Boolean) as string[];
  const galleryUnique = Array.from(new Set(galleryList));
  const gallery = galleryUnique.length > 0 ? galleryUnique : (imageUrl ? [imageUrl] : []);

  const blocksList = Array.isArray((dev as any).blocks) ? (dev as any).blocks : [];
  const firstBlock = blocksList.length > 0 ? blocksList[0] : null;
  const blockPrice = toNumber(firstBlock?.unit_price);
  const minBlocks = toNumber(dev.min_blocks_per_order);
  const maxBlocks = toNumber(dev.max_blocks_per_user);
  
  // Calcular valores basados en datos reales cuando estén disponibles
  const fundingProgress = dev.progress_pct || 0;
  const returnRate = 12; // Por defecto, en futuro obtener del backend
  const minimumInvestment = 50000; // Por defecto, en futuro obtener del backend
  
  return {
    id: dev.id,
    title: dev.name,
    location: `${normalizeMexico(dev.city)}, ${normalizeMexico(dev.state)}`,
    returnRate,
    minimumInvestment,
    fundingProgress,
    imageUrl,
    gallery,
    minBlocks,
    maxBlocks,
    blockPrice: Number.isFinite(blockPrice as number) ? (blockPrice as number) : null,
    isFeatured: dev.status === 'PRE_SALE', // Los proyectos en pre-venta como destacados
    description:
      normalizeMexico(dev.description) ||
      `Desarrollo ${dev.name} en ${normalizeMexico(dev.city)}, ${normalizeMexico(dev.state)}. Proyecto con status ${dev.status}.`,
    status: 'Activo', // Mapear todos los activos como 'Activo'
    videoUrl: typeof dev.video_url === 'string' ? dev.video_url : undefined,
    documents: Array.isArray(dev.documents)
      ? dev.documents.map(d => ({ title: d.title, file_url: resolveFileUrl(d.file_url) }))
      : undefined,
    projectState: dev.project_state,
    requiredDocumentsCount: Number((dev as any).requiredDocumentsCount ?? 0),
    uploadedDocumentsCount: Number((dev as any).uploadedDocumentsCount ?? 0),
    approvedDocumentsCount: Number((dev as any).approvedDocumentsCount ?? 0),
    missingDocumentsCount: Number((dev as any).missingDocumentsCount ?? 0),
    documentsSubmitted: Boolean((dev as any).documentsSubmitted),
    documentsApproved: Boolean((dev as any).documentsApproved),
    investmentStart: dev.investment_start_at ? String(dev.investment_start_at) : undefined,
    investmentEnd: dev.investment_end_at ? String(dev.investment_end_at) : undefined,
    fundingGoal: toNumber(dev.funding_goal),
    termMonths: toNumber(dev.term_months)
  };
};
